import { Injectable, inject } from '@angular/core';
import { Action, Selector, State } from '@ngxs/store';
import type { StateContext } from '@ngxs/store';
import { EMPTY, Observable, forkJoin, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ScdApiService } from '../../services/scd-api.service';
import { ResolveGids } from './scd.actions';

export interface ScdStateModel {
    /** Maps GID → resolved display name (firstName + surName). */
    displayNames: Record<string, string>;
    /** GIDs whose SCD lookup returned no result or failed — user is no longer in the directory. */
    failed: Set<string>;
    /** GIDs for which a request is currently in flight — avoids duplicate parallel calls. */
    resolving: Set<string>;
}

@State<ScdStateModel>({
    name: 'scdState',
    defaults: {
        displayNames: {},
        failed: new Set(),
        resolving: new Set()
    }
})
@Injectable()
export class ScdState {
    private readonly scdApi = inject(ScdApiService);

    /** Full display names map — useful for bulk reads. */
    @Selector()
    static displayNames(state: ScdStateModel): Record<string, string> {
        return state.displayNames;
    }

    /** Set of GIDs that could not be resolved (user not found or API error). */
    @Selector()
    static failedGids(state: ScdStateModel): Set<string> {
        return state.failed;
    }

    @Action(ResolveGids)
    public resolveGids(ctx: StateContext<ScdStateModel>, { gids }: ResolveGids): Observable<unknown> {
        const { displayNames, failed, resolving } = ctx.getState();

        // Skip GIDs that are already resolved, already known to have failed, or in flight.
        const pending = [...new Set(gids)].filter(
            gid => gid && !(gid in displayNames) && !failed.has(gid) && !resolving.has(gid)
        );

        if (pending.length === 0) {
            return EMPTY;
        }

        // Mark all as in-flight.
        const newResolving = new Set(resolving);
        pending.forEach(gid => newResolving.add(gid));
        ctx.patchState({ resolving: newResolving });

        // Resolve all GIDs in parallel; individual failures are caught and recorded as failed.
        const requests = pending.map(gid => this.scdApi.getUserByGid(gid).pipe(catchError(() => of(null))));

        return forkJoin(requests).pipe(
            tap(results => {
                const { displayNames: current, failed: currentFailed, resolving: currentResolving } = ctx.getState();
                const updated = { ...current };
                const nowFailed = new Set(currentFailed);
                const stillResolving = new Set(currentResolving);

                results.forEach((user, i) => {
                    const gid = pending[i];
                    if (user) {
                        updated[gid] = `${user.firstName} ${user.surName}`.trim();
                    } else {
                        // null means not found or request failed — mark as failed so the
                        // component can show the appropriate "no longer with Siemens" label.
                        nowFailed.add(gid);
                    }
                    stillResolving.delete(gid);
                });

                ctx.patchState({ displayNames: updated, failed: nowFailed, resolving: stillResolving });
            })
        );
    }
}
